import { fmtMoney, fmtSignedMoney, fmtPct, fmtNum, pnlClass, fmtHold } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

export function Sparkline({ points = [], className = "", stroke = "currentColor", width = 96, height = 28 }) {
  if (!points || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(height - 2 - ((p - min) / span) * (height - 4)).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Delta({ value, suffix = "%" }) {
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium tabular-nums", pnlClass(value))}>
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(value).toFixed(Math.abs(value) >= 100 ? 0 : 2)}
      {suffix}
    </span>
  );
}

function Big({ children, className }) {
  return (
    <div className={cn("font-display text-2xl font-semibold tracking-tight tabular-nums md:text-3xl", className)}>
      {children}
    </div>
  );
}

function Sub({ children }) {
  return <div className="mt-1 text-xs text-muted-foreground">{children}</div>;
}

function RiskGauge({ risk, compact }) {
  const score = risk?.score || 1;
  const colors = ["bg-[hsl(var(--profit))]", "bg-[hsl(var(--profit))]", "bg-[hsl(var(--warning))]", "bg-[hsl(var(--warning))]", "bg-[hsl(var(--loss))]"];
  return (
    <div>
      <Big className={score >= 4 ? "text-[hsl(var(--loss))]" : score >= 3 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--profit))]"}>
        {risk?.label || "-"}
      </Big>
      <div className="mt-2 flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={cn("h-1.5 flex-1 rounded-full", i <= score ? colors[score - 1] : "bg-muted")} />
        ))}
      </div>
      {!compact && <Sub>Max drawdown {fmtPct(risk?.max_drawdown)}</Sub>}
    </div>
  );
}

/**
 * Renders any of the 12 stat widget types from the shared analytics payload.
 */
export function StatWidget({ type, data, compact }) {
  const s = data?.summary;
  const cur = data?.account?.currency || "USD";
  if (!s) return null;

  switch (type) {
    case "balance":
      return (
        <div>
          <Big>{fmtMoney(s.balance, cur)}</Big>
          {!compact && <Sub>Equity {fmtMoney(s.equity, cur)} · Floating {fmtSignedMoney(s.floating_pnl, cur)}</Sub>}
          <div className="mt-1"><Delta value={s.range_gain_pct} /></div>
        </div>
      );
    case "equity":
      return (
        <div>
          <Big>{fmtMoney(s.equity, cur)}</Big>
          <div className="mt-1">
            <span className={cn("text-xs font-medium tabular-nums", pnlClass(s.floating_pnl))}>
              {fmtSignedMoney(s.floating_pnl, cur)} floating
            </span>
          </div>
          {!compact && <Sub>{s.open_trades} open position{s.open_trades === 1 ? "" : "s"}</Sub>}
        </div>
      );
    case "total_gain":
      return (
        <div>
          <Big className={pnlClass(s.total_gain_pct)}>{fmtPct(s.total_gain_pct, true)}</Big>
          {!compact && <Sub>Since inception · {fmtSignedMoney(s.balance - data.account.start_balance, cur)}</Sub>}
          {compact && <Sub>all-time</Sub>}
        </div>
      );
    case "monthly_profit":
      return (
        <div>
          <Big className={pnlClass(s.monthly_profit)}>{fmtSignedMoney(s.monthly_profit, cur)}</Big>
          <Sub>this calendar month</Sub>
        </div>
      );
    case "win_rate": {
      const wins = Math.round((s.win_rate / 100) * s.total_trades);
      return (
        <div>
          <Big>{s.win_rate}%</Big>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-[hsl(var(--profit))]" style={{ width: `${s.win_rate}%` }} />
          </div>
          {!compact && <Sub>{fmtNum(wins)} won · {fmtNum(s.total_trades - wins)} lost</Sub>}
        </div>
      );
    }
    case "profit_factor":
      return (
        <div>
          <Big className={s.profit_factor >= 1 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"}>
            {s.profit_factor}
          </Big>
          {!compact ? <Sub>${""}won per $1 lost · {s.profit_factor >= 1.4 ? "strong edge" : s.profit_factor >= 1 ? "profitable" : "losing"}</Sub> : <Sub>profit factor</Sub>}
        </div>
      );
    case "max_drawdown":
      return (
        <div>
          <Big className="text-[hsl(var(--loss))]">-{fmtPct(s.max_drawdown)}</Big>
          {!compact && <Sub>Current {fmtPct(Math.abs(s.current_drawdown))} below peak</Sub>}
          {compact && <Sub>max drawdown</Sub>}
        </div>
      );
    case "risk_level":
      return <RiskGauge risk={s.risk} compact={compact} />;
    case "total_trades":
      return (
        <div>
          <Big>{fmtNum(s.total_trades)}</Big>
          {!compact && (
            <Sub>
              {fmtNum(s.longs)} longs ({s.longs ? Math.round((s.longs_won / s.longs) * 100) : 0}% won) · {fmtNum(s.shorts)} shorts ({s.shorts ? Math.round((s.shorts_won / s.shorts) * 100) : 0}% won)
            </Sub>
          )}
          {compact && <Sub>closed trades</Sub>}
        </div>
      );
    case "avg_win_loss":
      return (
        <div className="flex items-end gap-4">
          <div>
            <div className="font-display text-xl font-semibold tabular-nums text-[hsl(var(--profit))] md:text-2xl">
              {fmtMoney(s.avg_win, cur)}
            </div>
            <Sub>avg win</Sub>
          </div>
          <div>
            <div className="font-display text-xl font-semibold tabular-nums text-[hsl(var(--loss))] md:text-2xl">
              {fmtMoney(s.avg_loss, cur)}
            </div>
            <Sub>avg loss</Sub>
          </div>
        </div>
      );
    case "pips_won":
      return (
        <div>
          <Big className={pnlClass(s.pips_total)}>{s.pips_total > 0 ? "+" : ""}{fmtNum(s.pips_total, 1)}</Big>
          {!compact ? <Sub>net pips · {fmtNum(s.lots, 1)} lots traded</Sub> : <Sub>net pips</Sub>}
        </div>
      );
    case "expectancy":
      return (
        <div>
          <Big className={pnlClass(s.expectancy)}>{fmtSignedMoney(s.expectancy, cur)}</Big>
          {!compact ? <Sub>per trade · avg hold {fmtHold(s.avg_hold_minutes)}</Sub> : <Sub>per trade</Sub>}
        </div>
      );
    default:
      return <Big>-</Big>;
  }
}
